// src/app/AppNavParts.tsx
/**
 * Presentational nav primitives: NavGroup and NavItem.
 *
 * Extracted from AppNav.tsx (plans/monolith-migration-plan.md §11.12, Batch 4).
 * Both are stateless and receive everything via props, so the move is mechanical.
 * AppNav.tsx re-exports them, so no importer changed.
 */

import React from "react";
import { ChevronDown, GripVertical } from "lucide-react";
/**
 * A collapsible navigation group component used in the side navigation bar.
 * Supports rendering children (navigation items) and toggling open/close states.
 * Has distinct layouts for Classic (v1) and Modern (v2) UI versions.
 *
 * @param {Object} props - Component properties.
 * @param {string} props.name - Display name of the navigation group.
 * @param {string} [props.color] - Color value for the visual dot indicator.
 * @param {boolean} props.expanded - Whether the group is currently expanded/open.
 * @param {Function} props.onToggle - Event handler function triggered on click to toggle expansion.
 * @param {React.ReactNode} props.children - Child elements (typically NavItem components).
 * @returns {JSX.Element} The rendered NavGroup component.
 */
export function NavGroup({
  name,
  color,
  expanded,
  onToggle,
  children,
  isCollapsed = false,
  blockId,
  isDraggable = false,
  isDragging = false,
  isDragOver = false,
  dropPosition = null,
  isJoinTarget = false,
  onDragStart,
  onDragOver,
  onDragLeave,
  onDrop,
  onDragEnd,
  onBodyDragOver,
  onBodyDragLeave,
  onBodyDrop,
}: {
  name: string;
  color?: string;
  expanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
  isCollapsed?: boolean;
  blockId?: string;
  isDraggable?: boolean;
  isDragging?: boolean;
  isDragOver?: boolean;
  dropPosition?: "before" | "after" | null;
  isJoinTarget?: boolean;
  onDragStart?: (e: React.DragEvent) => void;
  onDragOver?: (e: React.DragEvent) => void;
  onDragLeave?: (e: React.DragEvent) => void;
  onDrop?: (e: React.DragEvent) => void;
  onDragEnd?: () => void;
  onBodyDragOver?: (e: React.DragEvent) => void;
  onBodyDragLeave?: (e: React.DragEvent) => void;
  onBodyDrop?: (e: React.DragEvent) => void;
}) {
  if (isCollapsed) {
    return (
      <div className="space-y-1 w-full flex flex-col items-center">
        {children}
      </div>
    );
  }
  return (
    <div
      data-block-id={blockId}
      className={`relative mb-2 transition-opacity ${
        isDragging ? "opacity-40" : ""
      }`}
    >
      {/* Drop indicator above the group header (reorder: land before it) */}
      {isDragOver && dropPosition === "before" && (
        <div className="absolute -top-1 left-2 right-2 h-0.5 bg-[#859F4A] rounded-full z-20 shadow-xs shadow-[#859F4A]/50 pointer-events-none" />
      )}

      {/*
        The HEADER is the reorder target: dropping here moves the dragged block
        before/after this group. Joining the group is the BODY's job below, so a
        loose tab can still be positioned above or between groups.
      */}
      <button
        onClick={onToggle}
        draggable={isDraggable}
        onDragStart={onDragStart}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        onDragEnd={onDragEnd}
        className={`w-full flex items-center justify-between px-4 py-2 text-[11px] font-bold text-slate-400 dark:text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors rounded-lg ${
          isDraggable ? "cursor-grab active:cursor-grabbing" : ""
        } ${
          isDragOver && !isJoinTarget
            ? "bg-[#859F4A]/10 ring-1 ring-[#859F4A]/50 text-slate-600 dark:text-slate-200"
            : ""
        }`}
      >
        <div className="flex items-center gap-2">
          {isDraggable && (
            <GripVertical
              size={12}
              className="shrink-0 opacity-40 hover:opacity-100"
            />
          )}
          <div
            className="w-2 h-2 rounded-full"
            style={{ backgroundColor: color || "var(--accent)" }}
          ></div>
          {name}
        </div>
        <ChevronDown
          size={14}
          className={`transition-transform duration-300 ${expanded ? "rotate-180" : ""}`}
        />
      </button>
      {expanded && (
        <div
          data-group-body={name}
          onDragOver={onBodyDragOver}
          onDragLeave={onBodyDragLeave}
          onDrop={onBodyDrop}
          className={`mt-1 ml-2 pl-2 border-l space-y-1 transition-colors rounded-lg ${
            isJoinTarget
              ? "border-[#859F4A] bg-[#859F4A]/10 ring-1 ring-inset ring-[#859F4A]/40"
              : "border-slate-100 dark:border-slate-800"
          }`}
        >
          {children}
        </div>
      )}

      {/* Drop indicator below the group header (reorder: land after it) */}
      {isDragOver && dropPosition === "after" && (
        <div className="absolute -bottom-1 left-2 right-2 h-0.5 bg-[#859F4A] rounded-full z-20 shadow-xs shadow-[#859F4A]/50 pointer-events-none" />
      )}
    </div>
  );
}

/**
 * An individual navigation menu button.
 * Supports rendering an icon, text label, active state styling, two notification
 * indicators (dot and badge pill), and optional drag-and-drop reordering.
 *
 * @param {Object} props - Component properties.
 * @param {React.ReactNode} props.icon - The visual icon element to display.
 * @param {string} props.label - Text label for the item.
 * @param {boolean} props.active - Whether the item is the currently selected active route.
 * @param {Function} props.onClick - Click handler function.
 * @param {boolean} [props.hasRedDot=false] - Whether to show a red dot notification badge.
 * @param {number} [props.badgeCount=0] - Numeric unread count; renders a pill when > 0.
 * @param {boolean} [props.isCollapsed=false] - Whether the parent sidebar is collapsed.
 * @param {boolean} [props.isDraggable=false] - Whether this item can be dragged.
 * @param {boolean} [props.isDragging=false] - Whether this item is currently being dragged.
 * @param {boolean} [props.isDragOver=false] - Whether another item is dragging over this item.
 * @param {"before"|"after"|null} [props.dropPosition=null] - Where the dropped item will land relative to this item.
 * @returns {JSX.Element} The rendered NavItem component.
 */
export function NavItem({
  icon,
  label,
  active,
  onClick,
  hasRedDot = false,
  badgeCount = 0,
  isCollapsed = false,
  isDraggable = false,
  isDragging = false,
  isDragOver = false,
  dropPosition = null,
  tabId,
  onDragStart,
  onDragOver,
  onDragLeave,
  onDrop,
  onDragEnd,
  onTouchStart,
  onTouchMove,
  onTouchEnd,
  onTouchCancel,
}: {
  icon: React.ReactNode;
  label: string;
  active: boolean;
  onClick: () => void;
  hasRedDot?: boolean;
  badgeCount?: number;
  isCollapsed?: boolean;
  isDraggable?: boolean;
  isDragging?: boolean;
  isDragOver?: boolean;
  dropPosition?: "before" | "after" | null;
  tabId?: string;
  onDragStart?: (e: React.DragEvent) => void;
  onDragOver?: (e: React.DragEvent) => void;
  onDragLeave?: (e: React.DragEvent) => void;
  onDrop?: (e: React.DragEvent) => void;
  onDragEnd?: () => void;
  onTouchStart?: (e: React.TouchEvent) => void;
  onTouchMove?: (e: React.TouchEvent) => void;
  onTouchEnd?: () => void;
  onTouchCancel?: () => void;
}) {
  const showBadge = badgeCount > 0;
  return (
    <div
      data-tab-id={tabId}
      data-block-id={tabId}
      draggable={isDraggable}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      onDragEnd={onDragEnd}
      className={`relative w-full group group/nav-item transition-all ${
        isDraggable ? "cursor-grab active:cursor-grabbing" : ""
      } ${isCollapsed ? "flex justify-center" : ""}`}
    >
      {/* Drop indicator above */}
      {isDragOver && dropPosition === "before" && (
        <div className="absolute -top-1 left-2 right-2 h-0.5 bg-[#859F4A] rounded-full z-20 shadow-xs shadow-[#859F4A]/50 pointer-events-none animate-in fade-in" />
      )}

      <div
        role="button"
        tabIndex={0}
        onClick={onClick}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onClick();
          }
        }}
        title={isCollapsed ? label : undefined}
        className={`w-full flex items-center text-left ${
          isCollapsed ? "justify-center px-2 py-3.5" : "gap-3 px-4 py-3"
        } rounded-xl transition-all select-none cursor-pointer ${
          isDragging
            ? "opacity-30 scale-[0.98] ring-2 ring-dashed ring-[#859F4A]/60"
            : active
            ? "bg-[#859F4A] text-white shadow-lg shadow-[#859F4A]/30 font-semibold"
            : "text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800/80 dark:hover:text-white"
        }`}
      >
        <div className={`relative flex items-center ${isCollapsed ? "justify-center" : "gap-3 w-full"}`}>
          {icon}
          {!isCollapsed && <span className="text-sm truncate">{label}</span>}
          {hasRedDot && (
            <span
              className={`absolute ${
                isCollapsed ? "-top-1 -right-1" : "right-0"
              } w-2.5 h-2.5 rounded-full bg-rose-500 animate-pulse`}
            />
          )}
          {showBadge && (
            <span
              className={`absolute ${
                isCollapsed ? "-top-1.5 -right-1.5" : "right-0"
              } min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-rose-500 text-white text-[10px] font-bold leading-none shadow-xs`}
            >
              {badgeCount > 99 ? "99+" : badgeCount}
            </span>
          )}
          {!isCollapsed && isDraggable && (
            <div
              aria-label={label ? `${label} verschieben` : "Reihenfolge ändern"}
              title="Gedrückt halten zum Verschieben"
              className={`ml-auto shrink-0 p-2 -mr-2 -my-2 flex items-center justify-center cursor-grab active:cursor-grabbing touch-none select-none transition-opacity ${
                active
                  ? "text-white opacity-85 hover:opacity-100"
                  : "text-slate-400 dark:text-slate-500 opacity-60 md:opacity-0 md:group-hover:opacity-60 hover:!opacity-100"
              }`}
              onTouchStart={onTouchStart}
              onTouchMove={onTouchMove}
              onTouchEnd={onTouchEnd}
              onTouchCancel={onTouchCancel}
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <GripVertical size={14} />
            </div>
          )}
        </div>
      </div>

      {/* Drop indicator below */}
      {isDragOver && dropPosition === "after" && (
        <div className="absolute -bottom-1 left-2 right-2 h-0.5 bg-[#859F4A] rounded-full z-20 shadow-xs shadow-[#859F4A]/50 pointer-events-none animate-in fade-in" />
      )}
    </div>
  );
}
